import { Component, EventEmitter, OnInit, Output, Inject } from '@angular/core';
import { AssessmentsService as CmbAssessmentsApi, Assessment, AssessmentResponse, AssessmentScreenBodyPart, AssessmentScreenPartTextInput, AssessmentScreenPartVideo, AssessmentScreenPartVideoAllOfVideo, AssessmentScreenPartChoice, AssessmentScreenPartImageAllOfStyle, AssessmentScreenPartSubmitGoToNext, AssessmentResponseAnswer, AssessmentScreenPartCannedChoice } from '@bluecrew/crew-member-backend-angular-client';
import { Router, ActivatedRoute } from '@angular/router';
import { LoadingController, ToastController } from '@ionic/angular';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { isNil } from 'lodash-es';

import { presentLoader } from '../util/ion-util';
import { SegmentCMOSService } from '../services/segment-cmos.service';
import { DOCUMENT } from '@angular/common';
import { CssImageStyle } from '../models/assessment';
import { UrlHistoryService } from '../services/url-history.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { UsStates } from '../util/us-states';
import { Browser } from '@capacitor/browser';
import { phoneValidationRegex } from '../util/phone-util';
import { isAnyI9Assessment } from '../util/assessment-util';

const DONE_INDEX = -1;

enum ToastType {
  ERROR,
  SUCCESS
}

@Component({
  selector: 'app-assessment',
  templateUrl: './assessment.page.html',
  styleUrls: ['./assessment.page.scss']
})
export class AssessmentPageComponent implements OnInit {

  @Output() readonly previousEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() readonly dismissEvent: EventEmitter<any> = new EventEmitter<any>();

  assessmentScreenBodyPartPages: Array<Array<AssessmentScreenBodyPart>>;
  assessmentScreenFormGroups: Array<UntypedFormGroup>;
  assessmentScreenBranches: Array<AssessmentScreenPartSubmitGoToNext | null>;
  assessmentIds: Array<string>;

  assessment: Assessment;
  response: AssessmentResponse;
  activeAssessmentScreen: number;
  totalAssessmentScreens: number;
  nextButtonClickedOnPage: boolean;
  nextButtonClickedOnPageSub: BehaviorSubject<boolean>;
  nextButtonClickedOnPage$: Observable<boolean>;
  correctAnswers: string[];
  incorrectAnswers: string[];
  checkAnswerClickedOnPage: boolean;
  disableNextButton: boolean;
  shouldDisplaySteps: boolean;
  isFromApplicationPage: boolean;
  stateChoices;

  constructor(private loadingCtrl: LoadingController,
              private toastCtrl: ToastController,
              private router: Router,
              private formBuilder: UntypedFormBuilder,
              private cmbAssessmentsApi: CmbAssessmentsApi,
              private segmentCMOS: SegmentCMOSService,
              private route: ActivatedRoute,
              private urlHistory: UrlHistoryService,
              @Inject(DOCUMENT) private document: Document) {
    const providedState = this.router.getCurrentNavigation().extras.state;
    this.assessment = providedState?.assessment;
    this.response = providedState?.response;
    this.isFromApplicationPage = providedState?.isFromApplicationPage || false;
  }

  async ngOnInit() {
    if (!this.assessment || !this.response) {
      const assessmentResponseId = this.route.snapshot.paramMap.get('assessmentResponseId');
      if (!assessmentResponseId) {
        return this.onDismiss();
      }
      const loader = await presentLoader(this.loadingCtrl);
      try {
        const { response, assessment } = await this.cmbAssessmentsApi.getAssessmentResponse(assessmentResponseId).toPromise();
        this.response = response;
        this.assessment = assessment;
      } catch (err) {
        let message = err?.error?.details ? err.error.details : 'Error getting assessment'
        this.presentToast(`${JSON.stringify(message)}`.replace(/"/g, ''), ToastType.ERROR);
        this.segmentCMOS.addEvent('assessmentPage_loadFailure', {
          assessmentResponseId: assessmentResponseId,
          reason: message || err.message || 'Unknown'
        });
        return this.onDismiss();
      } finally {
        loader.dismiss();
      }
    }
    this.activeAssessmentScreen = 0;
    this.totalAssessmentScreens = this.assessment.screens.length;
    this.nextButtonClickedOnPage = false;
    this.nextButtonClickedOnPageSub = new BehaviorSubject<boolean>(null);
    this.nextButtonClickedOnPage$ = this.nextButtonClickedOnPageSub.asObservable();
    this.assessmentScreenFormGroups = [];
    this.assessmentScreenBranches = [];
    this.assessmentScreenBodyPartPages = [];
    this.assessmentIds = [];
    this.incorrectAnswers = [];
    this.correctAnswers = [];
    this.checkAnswerClickedOnPage = false;
    this.disableNextButton = false;
    this.shouldDisplaySteps = true;

    this.assessment.screens.forEach((screen, index) => {
      this.assessmentScreenBodyPartPages.push(screen.bodyParts);
      this.assessmentIds.push(screen.id || index.toString());
      this.assessmentScreenBranches.push(screen.submit.goToNext);
      this.assessmentScreenFormGroups.push(this.buildForm(this.assessmentScreenBodyPartPages[index]));
      if (screen.submit.goToNext) {
        this.shouldDisplaySteps = false;
      }
    });
    this.hydrateWithExistingAnswers();
  }

  private hydrateWithExistingAnswers() {
    let isActiveScreenImmutable = false;

    this.response.answers?.forEach((answerScreen, screenIdx) => {
      const formGroup = this.assessmentScreenFormGroups[screenIdx];
      const formParts = this.assessmentScreenBodyPartPages[screenIdx];
      if (answerScreen?.answerParts?.length !== formParts.length && answerScreen?.status !== 'skipped') {
        // if the lengths don't match, then it's incomplete.
        // if the status is skipped, ignore the length.
        if (!isActiveScreenImmutable) this.activeAssessmentScreen = screenIdx;
        isActiveScreenImmutable = true;
      } else {
        // if the lengths match, then the form was valid when it was updated in the past
        if (!isActiveScreenImmutable) this.activeAssessmentScreen = Math.min(screenIdx + 1, this.totalAssessmentScreens - 1);
      }
      answerScreen?.answerParts?.forEach((answerPart, answerIndex) => {
        if (answerPart?.values) {
          formGroup.patchValue({
            // TODO for cases where we allow multiple values we need to probably update the line below
            [formParts[answerIndex]._object_type + answerIndex]: answerPart.values[0],
          });
        }
      });
    });
  }


  buildForm(formContent: Array<AssessmentScreenBodyPart>): UntypedFormGroup {
    const formGroup = {};
    formContent.forEach((screenPart, index) => {
      if (screenPart._object_type === 'AssessmentScreenPartTextInput') {
        let textInput = screenPart as AssessmentScreenPartTextInput
        let required = textInput.inputValidation && textInput.inputValidation.minLength && textInput.inputValidation.minLength > 0;
        switch (textInput.inputType) {
          case 'phone':
            if (required) {
              formGroup[screenPart._object_type + index] = new UntypedFormControl('', Validators.compose([Validators.required, Validators.pattern(phoneValidationRegex)]));
            } else {
              formGroup[screenPart._object_type + index] = new UntypedFormControl('', Validators.compose([Validators.pattern(phoneValidationRegex)]));
            }
            break;
          case 'email':
            if (required) {
              formGroup[screenPart._object_type + index] = new UntypedFormControl('', Validators.compose([Validators.required, Validators.email, Validators.pattern(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/)]));
            } else {
              formGroup[screenPart._object_type + index] = new UntypedFormControl('', Validators.compose([Validators.email, Validators.pattern(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/)]));
            }
            break;
          case 'zipCode':
            if (required) {
              formGroup[screenPart._object_type + index] = new UntypedFormControl('', Validators.compose([Validators.required, Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/)]));
            } else {
              formGroup[screenPart._object_type + index] = new UntypedFormControl('', Validators.compose([Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/)]));
            }
            break;
          case 'name':
            if (required) {
              formGroup[screenPart._object_type + index] = new UntypedFormControl('', Validators.compose([Validators.required, Validators.pattern(/^[a-zA-Z-.'' ]*$/)]));
            } else {
              formGroup[screenPart._object_type + index] = new UntypedFormControl('', Validators.compose([Validators.pattern(/^[a-zA-Z-.'' ]*$/)]));
            }
            break;
          default:
            if (textInput.inputValidation && textInput.inputValidation.maxLength && textInput.inputValidation.maxLength > 0 && textInput.inputValidation.minLength && textInput.inputValidation.minLength > 0) {
              if (required && textInput.inputValidation.regExp) {
                formGroup[screenPart._object_type + index] = new UntypedFormControl('', Validators.compose([Validators.required, Validators.minLength(textInput.inputValidation.minLength), Validators.maxLength(textInput.inputValidation.maxLength), Validators.pattern(textInput.inputValidation.regExp)]));
              } else if (textInput.inputValidation.regExp) {
                formGroup[screenPart._object_type + index] = new UntypedFormControl('', Validators.compose([Validators.minLength(textInput.inputValidation.minLength), Validators.maxLength(textInput.inputValidation.maxLength), Validators.pattern(textInput.inputValidation.regExp)]));
              } else if (required) {
                formGroup[screenPart._object_type + index] = new UntypedFormControl('', Validators.compose([Validators.required, Validators.minLength(textInput.inputValidation.minLength), Validators.maxLength(textInput.inputValidation.maxLength)]));
              } else {
                formGroup[screenPart._object_type + index] = new UntypedFormControl('', Validators.compose([Validators.maxLength(textInput.inputValidation.maxLength)]));
              }
            } else {
              formGroup[screenPart._object_type + index] = new UntypedFormControl('');
            }
            if (textInput.verifyAssessmentScreenPartTextIndex != null) {
              formGroup[screenPart._object_type + index].setValidators(
                [this.verifyTextInputValidator(screenPart, index), formGroup[screenPart._object_type + index].validator]
              );
            }
            break;
          }
      } else if (screenPart._object_type === 'AssessmentScreenPartChoice' || screenPart._object_type === 'AssessmentScreenPartCannedChoice') {
        if (screenPart.inputType === 'exactlyOne' || screenPart.inputType === 'oneOrMore') {
          formGroup[screenPart._object_type + index] = new UntypedFormControl('', Validators.required);
        } else {
          formGroup[screenPart._object_type + index] = new UntypedFormControl('');
        }
      } else if (screenPart._object_type === 'AssessmentScreenPartConsent') {
        formGroup[screenPart._object_type + index] = new UntypedFormControl('', Validators.required);
      } else {
        formGroup[screenPart._object_type + index] = new UntypedFormControl('');
      }
    });

    return this.formBuilder.group(formGroup);
  }

  verifyTextInputValidator(screenPart: AssessmentScreenPartTextInput, index: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const compareIndex = screenPart.verifyAssessmentScreenIndex !== null ? screenPart.verifyAssessmentScreenIndex
                                                                                : this.activeAssessmentScreen;
      if (this.assessmentScreenFormGroups[compareIndex]?.controls[screenPart._object_type + screenPart.verifyAssessmentScreenPartTextIndex].value !==
       this.assessmentScreenFormGroups[this.activeAssessmentScreen]?.controls[screenPart._object_type + index].value) {
        return {textVerify: false};
       }
       return null;
    }
  }

  async trackSegmentData(formParts: AssessmentScreenBodyPart[]) {
    formParts.forEach(async formPart => {
      switch (formPart._object_type) {
        case 'AssessmentScreenPartVideo':
          this.segmentCMOS.addEvent('assessmentScreenPartVideo_watched', {
            videoLabel: (formPart as AssessmentScreenPartVideo).label,
            videoUrl: ((formPart as AssessmentScreenPartVideo).video as AssessmentScreenPartVideoAllOfVideo).url
          });
          break;
        default:
          break;
      }
    });
  }

  async presentToast(toastMessage: string, type?: ToastType) {
    // Stop multiple toasts
    try {
      await this.toastCtrl.dismiss();
    } catch(err) {}
      let toast;
    if (type === ToastType.SUCCESS) {
      toast = await this.toastCtrl.create({
        cssClass: 'success-toast',
        message: '<img src="assets/icons/completed-check.svg"></img><span>' + toastMessage + '</span>',
        duration: 5000,
        position: 'bottom',
      });
    } else {
      toast = await this.toastCtrl.create({
        cssClass: 'assessment-toast',
        message: '<img src="assets/icons/alert-triangle.svg"></img>  ' + toastMessage,
        duration: 1500,
        position: 'bottom',
      });
    }
    toast.present();
  }

  // This function will re-check form values and their validity
  // This is needed for cases like the input text verification where if the field that is being verified
  // is modified after setting a valid "verify" field, the validator will not re-check. This forces the re-check
  // https://stackoverflow.com/questions/58832992/angular-6-this-formgroup-updatevalueandvalidity-not-working-properly
  validateFormGroup(formGroup: UntypedFormGroup): void {
    const controls = formGroup.controls;
    for (const key in controls) {
      const formElement = controls[key];
      if (formElement instanceof UntypedFormControl) {
        formElement.updateValueAndValidity();
      } else if (formElement instanceof UntypedFormGroup) {
        this.validateFormGroup(formElement);
      }
    }
  }

  async onSubmit() {
    const url = this.assessment.screens[this.activeAssessmentScreen]?.submit?.cta?.url
    const urlId = this.assessment.screens[this.activeAssessmentScreen]?.submit?.cta?.urlId
    if (url) {
      Browser.open({ url })
    } else if (urlId) {
      const dynamicUrl = await this.cmbAssessmentsApi.generateUrl(this.assessment.id, urlId).toPromise()
      Browser.open({ url: dynamicUrl?.url })
    }
    this.disableNextButton = true;
    this.nextButtonClickedOnPage = true;
    this.nextButtonClickedOnPageSub.next(this.nextButtonClickedOnPage);
    this.checkAnswerClickedOnPage = true
    const currentFormControl = this.assessmentScreenFormGroups[this.activeAssessmentScreen];
    this.validateFormGroup(currentFormControl); // needed to call for any verification text fields
    const formAnswers = currentFormControl.value;
    const formParts = this.assessmentScreenBodyPartPages[this.activeAssessmentScreen];
    const pendingAnswers = this.response.answers ? [...this.response.answers] : [];
    pendingAnswers[this.activeAssessmentScreen] = {
      answerParts: formParts.map((formPart, index) => {
        switch (formPart._object_type) {
          case 'AssessmentScreenPartCannedChoice':
          case 'AssessmentScreenPartChoice':
          case 'AssessmentScreenPartConsent':
          case 'AssessmentScreenPartDateTime':
          case 'AssessmentScreenPartTextInput':
          case 'AssessmentScreenPartUploadImage':
            // TODO for cases where we allow multiple values we need to probably update the line below
            return {values: [formAnswers[formPart._object_type + index]]};
          default:
            return {};
        }
      })
    };
    const nextIndex = this.getNextAssessmentScreenIndex(pendingAnswers[this.activeAssessmentScreen]);

    const oldAnswers = this.response.answers;
    this.response.answers = pendingAnswers;
    if (this.assessmentScreenFormGroups[this.activeAssessmentScreen].invalid) {
      this.disableNextButton = false;
      this.response.answers = oldAnswers;
      this.presentToast('See error messages', ToastType.ERROR);
      return;
    }

    const loader = await presentLoader(this.loadingCtrl);
    if (this.activeAssessmentScreen >= (this.totalAssessmentScreens - 1) || nextIndex === DONE_INDEX) {
      this.fillSkippedAnswers(this.assessmentScreenBodyPartPages.length);

      try {
        // Normally a CM can only reach this point when the assessment in draft state, as once the assessment is in
        // pending grade or done state, we no longer allow the CM to even open the assessment. However, there is an
        // exception for I9 assessments in pending grade state. At this point, the CM has completed Section 1 but may
        // still need access to their assessment so they can open EMPTrust, where they can update their supporting
        // documents or agent details. In this special case, do not call CMOS, since updating an assessment in pending
        // grade state will fail.
        if (isAnyI9Assessment(this) &&
            this.response.state === 'pendingGrade') {
          this.segmentCMOS.addEvent('i9_part1_emptrust_reopened');
        } else {
          await this.cmbAssessmentsApi.submitAssessmentResponse(this.response.id, this.response).toPromise();
        }

        loader.dismiss();
        this.document.defaultView.setTimeout(() => {
          this.onDismiss();
        }, this.isGuided() ? 1500 : 0);

        if (this.isGuided()) {
          this.correctAnswers.push(this.getSelectedAnswer())
        }

        if (!this.isFromApplicationPage) {
          this.presentToast(`${this.assessment.name} updated successfully`, ToastType.SUCCESS);
        }
      } catch(err) {
        loader.dismiss();
        this.document.defaultView.setTimeout(() => {
          this.disableNextButton = false;
        }, 1000);

        this.response.answers = oldAnswers;
        if (err?.error?.code === 'InvalidInput') {
          if(err?.error?.reason === 'IncorrectAnswer') {
            this.incorrectAnswers.push(this.getSelectedAnswer());
          } else {
            this.presentToast(err?.error?.toastMessage || 'See error messages', ToastType.ERROR);
          }
        }
      }
    } else {
      this.fillSkippedAnswers(nextIndex);
      await this.cmbAssessmentsApi.updateAssessmentResponse(this.response.id, this.response)
        .toPromise()
        .then(() => {
          loader.dismiss();
          this.document.defaultView.setTimeout(() => {
            this.disableNextButton = false;

            this.activeAssessmentScreen = nextIndex;
            this.nextButtonClickedOnPage = false;
            this.nextButtonClickedOnPageSub.next(this.nextButtonClickedOnPage);
            this.checkAnswerClickedOnPage = false;
            this.correctAnswers = []
            this.incorrectAnswers = []
          }, this.isGuided() ? 1500 : 0);

          if (this.isGuided()) {
            this.correctAnswers.push(this.getSelectedAnswer())
          }
        }).catch((err) => {
          loader.dismiss();
          this.document.defaultView.setTimeout(() => {
            this.disableNextButton = false;
          }, 1000);
          this.response.answers = oldAnswers;
          if (err?.error?.code === 'InvalidInput') {
            if (err?.error?.reason === 'IncorrectAnswer') {
              this.incorrectAnswers.push(this.getSelectedAnswer());
            } else {
              this.presentToast(err?.error?.toastMessage || 'See error messages', ToastType.ERROR);
            }
          }
          console.error(err);
        });
    }
    this.trackSegmentData(formParts);
  }

  async onPrevious() {
    if (this.activeAssessmentScreen <= 0) {
      return this.onDismiss();
    }
    this.activeAssessmentScreen--;
    let containsSkips = false;
    for (this.activeAssessmentScreen; this.activeAssessmentScreen >= 0; this.activeAssessmentScreen--) {
      if (this.response?.answers[this.activeAssessmentScreen]?.status === 'skipped') {
        containsSkips = true;
      } else {
        break;
      }
    }
    if (containsSkips) {
      this.response.answers = this.response.answers.slice(0, this.activeAssessmentScreen + 1);
    }
  }

  onDismiss(): void {
    this.urlHistory.goBackWithDefault();
  }

  trackByFn(index: number): number {
    return index;
  }

  getSelectedAnswer(): string {
    let answerChoice = Object.keys(this.assessmentScreenFormGroups[this.activeAssessmentScreen].value)
      .filter(k => k.startsWith('AssessmentScreenPartChoice')
    );
    return this.assessmentScreenFormGroups[this.activeAssessmentScreen].value[answerChoice[0]]
  }

  isGuided(): boolean {
    let assessmentChoices = this.assessment.screens[this.activeAssessmentScreen].bodyParts
      .filter(k => k._object_type.startsWith('AssessmentScreenPartChoice')
    );
    return assessmentChoices.length > 0 && !!((assessmentChoices[0] as AssessmentScreenPartChoice).answers)
  }

  // Sanitize passed style configurations.
  getImageStyle(imageStyle: AssessmentScreenPartImageAllOfStyle): CssImageStyle {
    return {
      'object-fit': imageStyle.objectFit
    };
  }

  getImageLink(imageUrl: string, linkUrl: string | undefined) {
    return linkUrl ? linkUrl : imageUrl;
  }

  /*
   * Returns the next index of the assessment.
   * If there are no custom branches, return index + 1.
   * If there are no custom branches that match the answer, return the index of the elseScreenId.
   * If there is a custom branch that matches the answer, return the index of the id specified.
   * If the assessmentId can not be found, return -1. This is understood as move to the end of the assessment.
   */
  getNextAssessmentScreenIndex(pendingAnswer: AssessmentResponseAnswer) {
    const branches = this.assessmentScreenBranches[this.activeAssessmentScreen];
    if (!branches) {
      return this.activeAssessmentScreen + 1;
    }
    let nextIndex: number;
    const answerIds = this.getAnswerIdsFromAnswer(pendingAnswer);
    branches.screenBasedOnAnswer.forEach((branch) => {
      if (branch.answerId && answerIds.includes(branch.answerId)) {
        nextIndex = this.assessmentIds.indexOf(branch.assessmentScreenId);
      }
    });
    if (isNil(nextIndex)) {
      nextIndex = this.assessmentIds.indexOf(branches.elseScreenId);
    }
    if (nextIndex > -1 && nextIndex <= this.activeAssessmentScreen) {
      this.segmentCMOS.addEvent('assessmentScreenPartGoTo_wrongDirection', {
        currentIndex: this.activeAssessmentScreen,
        nextIndex: nextIndex
      });
      throw new Error('Next assessment screen is before the current screen.');
    }
    return nextIndex;
  }

  getAnswerIdsFromAnswer(pendingAnswer: AssessmentResponseAnswer) {
    const assessmentScreenParts = this.assessmentScreenBodyPartPages[this.activeAssessmentScreen];
    return assessmentScreenParts
      .filter((screenPart): screenPart is AssessmentScreenPartChoice => screenPart._object_type === 'AssessmentScreenPartChoice')
      .flatMap(screenPart => screenPart.choices)
      .flatMap(choice => pendingAnswer.answerParts
        .filter(answerPart => answerPart.values?.includes(choice.text))
        .map(_ => choice.answerId)
      );
  }

  getSkippedAnswer() {
    return {
      status: "skipped"
    } as AssessmentResponseAnswer;
  }

  fillSkippedAnswers(endIndex: number) {
    if (this.activeAssessmentScreen + 1 < endIndex) {
      this.response.answers = this.response.answers.slice(0, this.activeAssessmentScreen + 1);
      for (let i = this.activeAssessmentScreen + 1; i < endIndex; i++) {
        this.response.answers.push(this.getSkippedAnswer());
      }
    }
  }

  ionChangeOnCheckboxChange(formControlName: string, choiceText: string, event: any) {
    if (event.detail.checked) {
      this.assessmentScreenFormGroups[this.activeAssessmentScreen].patchValue({
        [formControlName]: choiceText,
      });
    } else {
      this.assessmentScreenFormGroups[this.activeAssessmentScreen].patchValue({
          [formControlName]: "",
      });
    }
  }

  clickLegalAcceptanceCheckboxArea(formControlName: string, choiceText: string, event: any) {
    if (event.target instanceof HTMLAnchorElement) {
      // Do not check checkbox if <a> element link is clicked
      return;
    }
    if (this.assessmentScreenFormGroups[this.activeAssessmentScreen].controls[formControlName].valid) {
      this.assessmentScreenFormGroups[this.activeAssessmentScreen].patchValue({
          [formControlName]: "",
      });
    } else {
      this.assessmentScreenFormGroups[this.activeAssessmentScreen].patchValue({
        [formControlName]: choiceText,
      });
    }
  }

  getScreenPartChoices(choices: any) {
    if (typeof choices === 'string' && choices === AssessmentScreenPartCannedChoice.ChoicesEnum.States) {
      // stateChoices needs to be initialized as an instance variable because the ion-select dropdown requires object values
      // that are persistent as long as the dropdown is visible
      if (!this.stateChoices) {
        this.stateChoices = UsStates.map(state => { return {text: state.name }});
      }
      return this.stateChoices;
    }
    return choices;
  }

  isScreenPartCannedChoice(screenPart: AssessmentScreenPartChoice | AssessmentScreenPartCannedChoice): boolean {
    return screenPart._object_type === 'AssessmentScreenPartCannedChoice';
  }
}
